import type { BandwidthTestResult } from './bandwidth-test';
import type { DiagnosticSuiteResult, TestResult } from './diagnostic-test';
import { parseBrowserInfo } from './generate-report';

/**
 * Format a test result for JSON export
 */
function formatTestResult(test: TestResult) {
  return {
    testId: test.testId,
    testName: test.testName,
    concurrency: test.concurrency,
    fileCount: test.fileCount,
    totalBytes: test.totalBytes,
    totalDurationMs: test.totalDurationMs,
    // Goodput (overall): THE headline metric
    goodputOverallMbps: test.goodputOverallMbps,
    aggregated: {
      // Connection timing observability
      dnsObservablePercent: test.aggregated.dnsObservablePercent,
      tcpObservablePercent: test.aggregated.tcpObservablePercent,
      tlsObservablePercent: test.aggregated.tlsObservablePercent,
      // Connection timings (median of observable only)
      medianDnsTime: test.aggregated.medianDnsTime,
      medianTcpConnectTime: test.aggregated.medianTcpConnectTime,
      medianTlsHandshakeTime: test.aggregated.medianTlsHandshakeTime,
      // Download duration
      avgDownloadDuration: test.aggregated.avgDownloadDuration,
      // Per-file throughput distribution
      p10ThroughputMbps: test.aggregated.p10ThroughputMbps,
      p50ThroughputMbps: test.aggregated.p50ThroughputMbps,
      // TTFB percentiles (for latency analysis)
      p10Ttfb: test.aggregated.p10Ttfb,
      p50Ttfb: test.aggregated.p50Ttfb,
      p95Ttfb: test.aggregated.p95Ttfb,
      minTtfb: test.aggregated.minTtfb,
      maxTtfb: test.aggregated.maxTtfb,
      // Goodput (overall) - same as TestResult level, for convenience
      goodputOverallMbps: test.aggregated.goodputOverallMbps,
      // Protocol aggregation (HTTP/2 detection)
      protocolAggregation: test.aggregated.protocolAggregation,
      // Hostname aggregation (domain sharding effectiveness)
      hostnameAggregation: test.aggregated.hostnameAggregation,
      // Cache detection
      cachedRequestCount: test.aggregated.cachedRequestCount,
      uncachedRequestCount: test.aggregated.uncachedRequestCount,
    },
  };
}

/**
 * Generate a JSON export of diagnostic results for programmatic use
 */
export function generateJsonExport(
  result: DiagnosticSuiteResult,
  bandwidthResult?: BandwidthTestResult | null
): string {
  const exportData = {
    meta: {
      version: '1.0.0',
      exportedAt: new Date().toISOString(),
      testTimestamp: result.timestamp,
    },
    device: {
      userAgent: result.deviceInfo.userAgent,
      browser: parseBrowserInfo(result.deviceInfo.userAgent),
      platform: result.deviceInfo.platform,
      screen: {
        width: result.deviceInfo.screenWidth,
        height: result.deviceInfo.screenHeight,
        pixelRatio: result.deviceInfo.devicePixelRatio,
      },
      timezone: result.deviceInfo.timezone,
      hardwareConcurrency: result.deviceInfo.hardwareConcurrency,
      deviceMemory: result.deviceInfo.deviceMemory,
      connection: result.deviceInfo.connection,
    },
    network: {
      publicNetworkLikely: result.networkEnvironment.publicNetworkLikely,
      captivePortalDetected: result.networkEnvironment.captivePortalDetected,
      indicators: result.networkEnvironment.indicators,
    },
    speedTest: bandwidthResult
      ? {
          speedMbps: bandwidthResult.speedMbps,
          peakSpeedMbps: bandwidthResult.peakSpeedMbps,
          downloadedBytes: bandwidthResult.downloadedBytes,
          testDurationMs: bandwidthResult.testDurationMs,
          latency: bandwidthResult.latency,
          bufferbloatRatio: bandwidthResult.bufferbloatRatio,
          connectionCount: bandwidthResult.connectionCount,
        }
      : null,
    tests: {
      testA: formatTestResult(result.testA),
      testB: formatTestResult(result.testB),
      testC: formatTestResult(result.testC),
      testD: formatTestResult(result.testD),
    },
    analysis: {
      // Summary flags
      connectionLimitDetected: result.analysis.connectionLimitDetected,
      inspectionOverheadDetected: result.analysis.inspectionOverheadDetected,
      throttlingDetected: result.analysis.throttlingDetected,
      issues: result.analysis.issues,
      recommendations: result.analysis.recommendations,
      // HTTP/2-aware analysis
      overallConfidence: result.analysis.overallConfidence,
      protocolSummary: result.analysis.protocolSummary,
      connectionLimit: result.analysis.connectionLimit,
      inspectionOverhead: result.analysis.inspectionOverhead,
      throttling: result.analysis.throttling,
    },
    rawMetrics: {
      testA: result.testA.metrics,
      testB: result.testB.metrics,
      testC: result.testC.metrics,
      testD: result.testD.metrics,
    },
  };

  return JSON.stringify(exportData, null, 2);
}
